// src/components/Footer.jsx
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Divider,
  Link,
} from '@mui/material';
import {
  School,
  Facebook,
  Instagram,
  LinkedIn,
  YouTube,
  Email,
  LocationOn,
} from '@mui/icons-material';
import XIcon from '@mui/icons-material/X';

import { useNavigate } from 'react-router-dom';
import logoImage from '../assets/images/Logo.svg'; 

const Footer = ({
  brandName = 'PeerCampus',
  brandDescription = 'AI-powered campus community for skills, events, doubts, and lost & found.',
  logoPath = logoImage,
  showNewsletter = true,
  showSocialLinks = true,
  showContactInfo = true,
}) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  const [subscriptionPending, setSubscriptionPending] = useState(false); // pending confirmation state
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  const colors = {
    primary: '#0D1C4F',
    secondary: '#1E3A8A',
    accent: '#016f57',
    background: '#1A1A1A',
    text: '#FFFFFF',
    textSecondary: 'rgba(255,255,255,0.75)',
    textLight: 'rgba(255,255,255,0.6)',
    gradient: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)',
    hoverColor: '#016f57',
  };

  const socialLinks = [
    { icon: <Facebook />, color: '#1877f2', url: 'https://peercampus.in', label: 'Facebook' },
    { icon: <XIcon />, color: '#1da1f2', url: 'https://peercampus.in', label: 'Twitter' },
    { icon: <Instagram />, color: '#e4405f', url: 'https://peercampus.in', label: 'Instagram' },
    { icon: <LinkedIn />, color: '#0077b5', url: 'https://peercampus.in', label: 'LinkedIn' },
    { icon: <YouTube />, color: '#ff0000', url: 'https://peercampus.in', label: 'YouTube' }
  ];

  const contactInfo = [
    { icon: <Email sx={{ fontSize: 16 }} />, text: 'hello@peercampus.in', href: 'mailto:hello@peercampus.in', type: 'email' },
    { icon: <LocationOn sx={{ fontSize: 16 }} />, text: 'Lucknow, India', href: 'https://maps.google.com/?q=Lucknow,India', type: 'location' },
  ];

  // Combine product and platform links into one "Product" column
  const productLinks = [
    { label: 'Lost & Found', action: () => navigate('/lostfound') },
    { label: 'Skills', action: () => navigate('/skills') },
    { label: 'Doubts', action: () => navigate('/doubts') },
    { label: 'Events', action: () => navigate('/events') },
  ];

  const companyLinks = [
    { label: 'About Us', action: () => navigate('/about') },
  ];

  const supportLinks = [
    { label: 'Forgot Password', action: () => navigate('/forgot-password') },
    { label: 'Student Login', action: () => navigate('/login/student') },
    { label: 'Admin Login', action: () => navigate('/login/admin') },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', action: () => navigate('/privacy') },
    { label: 'Terms of Service', action: () => navigate('/terms') },
  ];

  // Email regex - simple but effective
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    if (!emailRegex.test(value)) return 'Enter a valid email address';
    return '';
  };

  const handleEmailChange = (e) => {
    const v = e.target.value;
    setEmail(v);
    setEmailError(validateEmail(v));
  };

  // Newsletter submission: performs client-side validation, simulates API sending confirmation email
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const err = validateEmail(email);
    setEmailError(err);
    if (err) {
      // announce error via live region (see below)
      return;
    }

    setLoading(true);
    setSubscriptionSuccess(false);
    setSubscriptionPending(false);

    try {
      // Replace with actual API call that triggers double opt-in email
      // Example: await api.post('/newsletter/subscribe', { email });

      // simulate api latency
      await new Promise((res) => setTimeout(res, 1000));

      // Simulate double opt-in: now in "pending confirmation" state
      setSubscriptionPending(true);
      setEmail('');
      // keep success false until user confirms through email (double opt-in)
      // We also expose an accessible message to inform the user.

    } catch (err) {
      // show a simple error fallback
      console.error(err);
      setEmailError('Subscription failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Small helper to render accessible link items with keyboard support
  const RenderLinkItem = ({ label, action }) => (
    <Typography
      onClick={action}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          action();
        }
      }}
      role="link"
      tabIndex={0}
      sx={{
        color: colors.textSecondary,
        mb: { xs: 0.75, md: 1 },
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize: '0.9rem',
        textAlign: 'left',
        '&:hover': {
          color: colors.hoverColor,
          textDecoration: 'underline'
        },
        '&:focus-visible': {
          outline: `3px solid ${colors.hoverColor}22`,
          borderRadius: '6px',
          bgcolor: `${colors.hoverColor}08`,
        }
      }}
    >
      {label}
    </Typography>
  );

  // Logo component
  const LogoComponent = () => {
    const [logoError, setLogoError] = useState(false);
    if (!logoError && logoPath) {
      return (
        <Box
          component="img"
          src={logoPath}
          alt={`${brandName} Logo`}
          onError={() => setLogoError(true)}
          sx={{
            height: { xs: 40, md: 56 },
            width: 'auto',
            cursor: 'pointer',
            display: 'block'
          }}
          onClick={() => navigate('/')}
        />
      );
    }
    return (
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: colors.gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={() => navigate('/')}
      >
        <School sx={{ color: 'white', fontSize: 32 }} />
      </Box>
    );
  };

  // JSON-LD Organization schema (structured data)
  // Uses window.location.origin when available; safe fallback to empty string
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const organizationJSON = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": brandName,
    "url": origin || "https://your-site.example", // replace fallback if you want
    "logo": origin ? `${origin}${logoPath}` : logoPath,
    "sameAs": socialLinks.map(s => s.url),
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": "hello@peercampus.in",
        "telephone": "+91-9876543210",
        "areaServed": "IN"
      }
    ]
  };

  return (
    <Box component="footer" sx={{ backgroundColor: colors.background, position: 'relative', overflow: 'hidden', mt: 'auto', width: '100%' }}>
      {/* JSON-LD structured data */}
      <Box component="script" type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJSON) }} />

      <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={{ xs: 2.25, md: 4 }}>
          <Grid item xs={12} md={5}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <LogoComponent />
                <Typography variant="h4" sx={{ fontWeight: 700, color: colors.text, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                  {brandName}
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2.25, lineHeight: 1.6, textAlign: { xs: 'center', md: 'left' } }}>
                {brandDescription}
              </Typography>

              {showSocialLinks && (
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  {socialLinks.map((social, i) => (
                    <IconButton
                      key={i}
                      component="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open ${social.label} (opens in new tab)`}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                        color: colors.text,
                        transition: 'background-color 0.2s ease, color 0.2s ease',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.10)' },
                        '&:focus-visible': { outline: `3px solid ${colors.hoverColor}22` }
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Grid container spacing={{ xs: 2.25, md: 3 }}>
              <Grid item xs={6} sm={4} md={4}>
                <Box sx={{ width: '100%', mx: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text, mb: 1.25, textAlign: 'left' }}>
                    Product
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {productLinks.map((link) => <RenderLinkItem key={link.label} label={link.label} action={link.action} />)}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={6} sm={4} md={4}>
                <Box sx={{ width: '100%', mx: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text, mb: 1.25, textAlign: 'left' }}>
                    Company
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {companyLinks.map((link) => <RenderLinkItem key={link.label} label={link.label} action={link.action} />)}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={6} sm={4} md={4}>
                <Box sx={{ width: '100%', mx: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text, mb: 1.25, textAlign: 'left' }}>
                    Support
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {supportLinks.map((l) => <RenderLinkItem key={l.label} label={l.label} action={l.action} />)}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={6} sx={{ display: { xs: 'block', sm: 'none' } }}>
                <Box sx={{ width: '100%', mx: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text, mb: 1.25, textAlign: 'left' }}>
                    Legal
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {legalLinks.map((link) => (
                      <RenderLinkItem key={link.label} label={link.label} action={link.action} />
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {(showNewsletter || showContactInfo) && (
          <Box sx={{ mt: { xs: 2.25, md: 3.25 } }}>
            <Grid container spacing={{ xs: 2.25, md: 3 }} alignItems="flex-start">
              {showNewsletter && (
                <Grid item xs={12} md={5}>
                  <Box sx={{ width: '100%', maxWidth: 520, mx: 0 }}>
                    <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                      Subscribe for updates (double opt-in)
                    </Typography>

                    <Box component="form" onSubmit={handleNewsletterSubmit} noValidate aria-labelledby="footer-newsletter-heading" sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                        <TextField
                          id="footer-newsletter-email"
                          name="email"
                          value={email}
                          onChange={handleEmailChange}
                          placeholder="Email"
                          aria-label="Email address"
                          aria-describedby={emailError ? 'footer-email-error' : undefined}
                          variant="outlined"
                          size="small"
                          type="email"
                          required
                          disabled={subscriptionPending || subscriptionSuccess}
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                              backgroundColor: 'rgba(255,255,255,0.04)',
                              '& fieldset': { borderColor: 'rgba(255,255,255,0.14)' },
                              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.22)' },
                              '&.Mui-focused fieldset': { borderColor: colors.hoverColor },
                              // ensure consistent height on all breakpoints
                              height: { xs: 44, sm: 44 },
                              display: 'flex',
                              alignItems: 'center',
                            },
                            '& .MuiInputBase-input': {
                              color: colors.text,
                              '::placeholder': { color: colors.textLight, opacity: 1 },
                              padding: '10px 12px',
                              height: '100%',
                              boxSizing: 'border-box',
                            },
                          }}
                        />

                        <Button
                          id="footer-newsletter-submit"
                          type="submit"
                          variant="contained"
                          disabled={loading || subscriptionPending || subscriptionSuccess}
                          sx={{
                            background: colors.gradient,
                            borderRadius: '10px',
                            px: { xs: 2, sm: 2.5 },
                            // match TextField height across breakpoints
                            height: { xs: 44, sm: 44 },
                            minHeight: 44,
                            lineHeight: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            width: { xs: '100%', sm: 'auto' },
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:disabled': { opacity: 0.6 }
                          }}
                          aria-disabled={loading || subscriptionPending || subscriptionSuccess}
                        >
                          {loading ? 'Sending…' : subscriptionPending ? 'Pending' : subscriptionSuccess ? 'Subscribed' : 'Subscribe'}
                        </Button>
                      </Box>

                      {emailError && (
                        <Typography id="footer-email-error" role="alert" sx={{ color: '#ffb4b4', mt: 1, fontSize: '0.875rem' }}>
                          {emailError}
                        </Typography>
                      )}

                      {subscriptionPending ? (
                        <Typography role="status" aria-live="polite" sx={{ color: colors.textSecondary, mt: 1, fontSize: '0.875rem' }}>
                          Confirmation sent — check your inbox to confirm.
                        </Typography>
                      ) : subscriptionSuccess ? (
                        <Typography role="status" aria-live="polite" sx={{ color: colors.textSecondary, mt: 1, fontSize: '0.875rem' }}>
                          Subscribed — thank you.
                        </Typography>
                      ) : null}

                      <Box role="status" aria-live="polite" sx={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}>
                        {emailError ? emailError : subscriptionPending ? 'Confirmation email sent' : subscriptionSuccess ? 'Subscription confirmed' : ''}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              )}

              {showContactInfo && (
                <Grid item xs={12} md={7}>
                  <Grid container spacing={{ xs: 0, md: 3 }}>
                    <Grid item xs={12} md={4} />
                    <Grid item xs={12} md={4}>
                      <Box sx={{ width: '100%', maxWidth: 520, mx: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: colors.text, mb: 1.25, textAlign: 'left' }}
                        >
                          Contact
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
                          {contactInfo.map((contact, idx) => (
                            <Box
                              key={idx}
                              component="a"
                              href={contact.href}
                              target={contact.type === 'location' ? '_blank' : undefined}
                              rel={contact.type === 'location' ? 'noopener noreferrer' : undefined}
                              itemProp={contact.type === 'email' ? 'email' : contact.type === 'phone' ? 'telephone' : undefined}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: colors.textSecondary,
                                textDecoration: 'none',
                                '&:hover': { color: colors.hoverColor, textDecoration: 'underline' },
                                '&:focus-visible': { outline: `3px solid ${colors.hoverColor}22`, borderRadius: '6px', bgcolor: `${colors.hoverColor}08` },
                                fontSize: '0.9rem'
                              }}
                              aria-label={`${contact.type === 'email' ? 'Send email to' : contact.type === 'phone' ? 'Call' : 'Open location'} ${contact.text}`}
                            >
                              <Box sx={{ color: colors.textSecondary }}>{contact.icon}</Box>
                              <Typography variant="body2" sx={{ color: 'inherit' }}>{contact.text}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Footer Bottom */}
        <Box>
          <Divider sx={{ my: { xs: 3, md: 4 }, borderColor: 'rgba(255,255,255,0.08)' }} />
          <Grid container spacing={3} alignItems="center" sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: colors.textLight, textAlign: { xs: 'center', md: 'left' } }}>
                © {new Date().getFullYear()} {brandName}. All rights reserved.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: { sm: 3 }, justifyContent: { sm: 'center', md: 'flex-end' }, flexWrap: 'wrap', width: '100%' }}>
                {legalLinks.map((item) => (
                  <Link key={item.label} onClick={item.action} component="button" underline="none" sx={{
                    color: colors.textLight, fontSize: '0.875rem', cursor: 'pointer', background: 'none', border: 'none', padding: 0,
                    '&:hover': { color: colors.hoverColor, textDecoration: 'underline' },
                    '&:focus-visible': { outline: `3px solid ${colors.hoverColor}22`, borderRadius: '6px', bgcolor: `${colors.hoverColor}08` }
                  }} aria-label={item.label}>
                    {item.label}
                  </Link>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
